import { sortByPriority } from "utils/sort";
import { DipMode } from "./types";
import { ActionType, Dip, DipType, PopUpVariant, Trigger } from "interfaces";

export const PREFIX = "dip";

const DIPS_WITH_OFFERS: ActionType[] = [
	"grantReward",
	"grantLoyaltyReward",
	"loyaltyPurchase",
	"offerPurchase"
];

export const getDIPtype = (
	currentDip: Dip | undefined
): DipType | undefined => {
	if (typeof currentDip === "undefined") {
		return undefined;
	}
	if (typeof currentDip.buttons === "undefined") {
		return "informative";
	}

	const { actionType } = currentDip.buttons[0];
	if (DIPS_WITH_OFFERS.includes(actionType)) {
		return "offers";
	}

	return "informative";
};

export const getDIPByTrigger = (
	list: Dip[],
	trigger: Trigger
): Dip | undefined => {
	const filteredByTrigger = list.filter(
		(dip) => dip.dip.triggerType === trigger
	);
	return sortByPriority(filteredByTrigger)?.[0];
};

export const getDIPByTriggerAndPricePoint = (
	list: Dip[],
	trigger: Trigger,
	pricePoint: number
): Dip | undefined => {
	const filteredDips = sortByPriority(
		list?.filter(
			(dip) => dip.dip.triggerType === trigger && dip.dipPurchaseTriggerConfig
		)
	);

	if (!filteredDips?.length) {
		return getDIPByTrigger(list, trigger);
	}

	const dipsWithPrice = filteredDips.filter(
		(dip) => !dip?.dipPurchaseTriggerConfig?.loyaltyConfig
	);

	if (dipsWithPrice.length) {
		const dipsWithPriceFound = dipsWithPrice.find((dip) => {
			const exactPrice = Number(dip?.dipPurchaseTriggerConfig?.pricePointUSD);
			const minPrice = Number(dip?.dipPurchaseTriggerConfig?.minPricePointUSD);
			const maxPrice = Number(dip?.dipPurchaseTriggerConfig?.maxPricePointUSD);

			if (exactPrice) {
				return exactPrice === pricePoint;
			} else if (!isNaN(minPrice) && isNaN(maxPrice)) {
				return minPrice <= pricePoint;
			} else if (isNaN(minPrice) && !isNaN(maxPrice)) {
				return maxPrice >= pricePoint;
			}

			return minPrice <= pricePoint && maxPrice >= pricePoint;
		});
		if (dipsWithPriceFound) {
			return dipsWithPriceFound;
		}
	}

	const dipsWithLoyalty = filteredDips.filter(
		(dip) => dip?.dipPurchaseTriggerConfig?.loyaltyConfig
	);

	if (dipsWithLoyalty.length) {
		const dipsWithLoyaltyFound = dipsWithLoyalty.find((dip) => {
			const exactLoyalty = Number(
				dip?.dipPurchaseTriggerConfig?.pricePointLoyalty
			);
			const minLoyalty = Number(
				dip?.dipPurchaseTriggerConfig?.minPricePointLoyalty
			);
			const maxLoyalty = Number(
				dip?.dipPurchaseTriggerConfig?.maxPricePointLoyalty
			);

			if (exactLoyalty) {
				return exactLoyalty === pricePoint;
			} else if (!isNaN(minLoyalty) && isNaN(maxLoyalty)) {
				return minLoyalty <= pricePoint;
			} else if (isNaN(minLoyalty) && !isNaN(maxLoyalty)) {
				return maxLoyalty >= pricePoint;
			}

			return minLoyalty <= pricePoint && maxLoyalty >= pricePoint;
		});
		if (dipsWithLoyaltyFound) {
			return dipsWithLoyaltyFound;
		}
	}
};

/***************************************************
 * Abort DIP execution
 ***************************************************/
interface Action {
	ABORT: boolean;
	CONTINUE: boolean;
}

const action: Action = {
	ABORT: true,
	CONTINUE: false
};

export const abortDIPExecution = (
	found: Dip,
	newTrigger: Trigger,
	mode: DipMode
): boolean => {
	const supressOnLanding =
		found.dip.suppressLandingPageDisplay && location.pathname === "/";

	if (supressOnLanding) {
		if (mode === "debug") {
			return action.CONTINUE;
		}
		if (
			newTrigger === "facebookLogout" ||
			newTrigger === "scopelyLogout" ||
			newTrigger === "bouncyChestClick" ||
			newTrigger.includes("purchaseSuccess-") ||
			newTrigger === "purchaseSuccess"
		) {
			return action.CONTINUE;
		}
		return action.ABORT;
	}
	return action.CONTINUE;
};

export const getAvailableTriggers = (list: Dip[]): Trigger[] => {
	const triggers: Trigger[] = [];
	list.forEach((dip) => {
		triggers.push(dip.dip.triggerType);
	});
	return triggers;
};

export type GrantRewardType = Extract<
	ActionType,
	"grantLoyaltyReward" | "grantReward"
>;

export const grantRewardPopUp: {
	[G in GrantRewardType]: PopUpVariant;
} = {
	grantLoyaltyReward: "loyalty-points-added",
	grantReward: "grant-reward-collected"
};
